import { v4 as uuidv4 } from 'uuid';

import {
  AgreementDTO,
  CommentDTO,
  CustomUserDTO,
  DirectoryDTO,
  FileDTO,
  FileUrlDTO,
  FileUrlResponse,
  FileValidationSettingsDTO,
  GlobalSettingsDTO,
  IndexDTO,
  InsuranceObjectDTO,
  ProcessDTO,
  ProcessStepDTO,
  TagDTO,
} from '@/types/api-types';
import type { ElasticSearchResponse } from '@/types/elastic-types';

import api from '@/utils/api';

import { API_ROUTES } from '@/constants/api-constants';
import { BASE_URL_API } from '@/constants/global-constants';

import { useWsClientStore, WsClientState } from './ws-client';

export class APIService {
  private kafkaClient: WsClientState;

  constructor(wsClient: WsClientState) {
    this.kafkaClient = wsClient;
  }

  private handleError(error: any): never {
    if (error.message === 'WebSocket is not connected') {
      throw new Error(
        'Service unavailable: WebSocket connection is not established',
      );
    }
    throw error;
  }

  async getUsers(
    uuids?: string[],
    extra?: Record<string, unknown>,
  ): Promise<CustomUserDTO[]> {
    try {
      return await this.kafkaClient.list<CustomUserDTO>('USER', uuids, extra);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getUser(uuid: string): Promise<CustomUserDTO> {
    try {
      return await this.kafkaClient.read<CustomUserDTO>('USER', uuid);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async createUser(
    userData: Omit<CustomUserDTO, 'uuid'>,
  ): Promise<CustomUserDTO> {
    try {
      return await this.kafkaClient.create<
        Omit<CustomUserDTO, 'uuid'>,
        CustomUserDTO
      >('USER', userData);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async updateUser(
    uuid: string,
    userData: Partial<CustomUserDTO>,
  ): Promise<CustomUserDTO> {
    try {
      return await this.kafkaClient.update<
        Partial<CustomUserDTO>,
        CustomUserDTO
      >('USER', uuid, userData);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async deleteUser(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('USER', uuid);
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Agreements
  async getAgreements(
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string; // insurance_object UUID
      exclude_archived?: boolean;
    },
  ): Promise<AgreementDTO[]> {
    try {
      return await this.kafkaClient.list<AgreementDTO>(
        'AGREEMENT',
        uuids,
        extra,
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  async getAgreement(uuid: string): Promise<AgreementDTO> {
    try {
      const response = await this.kafkaClient.read<AgreementDTO>(
        'AGREEMENT',
        uuid,
      );
      if (!response) {
        throw new Error('Agreement not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createAgreement(
    data: Omit<AgreementDTO, 'uuid'>,
  ): Promise<AgreementDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<AgreementDTO, 'uuid'>,
        AgreementDTO
      >('AGREEMENT', data);
      if (!response) {
        throw new Error('Failed to create agreement');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateAgreement(
    uuid: string,
    data: Partial<AgreementDTO>,
  ): Promise<AgreementDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<AgreementDTO>,
        AgreementDTO
      >('AGREEMENT', uuid, data);
      if (!response) {
        throw new Error('Failed to update agreement');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteAgreement(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('AGREEMENT', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Indexes
  async getIndexes(): Promise<IndexDTO[]> {
    try {
      return await this.kafkaClient.list<IndexDTO>('INDEX');
    } catch (error) {
      throw error;
    }
  }

  async getIndex(uuid: string): Promise<IndexDTO> {
    try {
      const response = await this.kafkaClient.read<IndexDTO>('INDEX', uuid);
      if (!response) {
        throw new Error('Index not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createIndex(data: Omit<IndexDTO, 'uuid'>): Promise<IndexDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<IndexDTO, 'uuid'>,
        IndexDTO
      >('INDEX', data);
      if (!response) {
        throw new Error('Failed to create index');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateIndex(uuid: string, data: Partial<IndexDTO>): Promise<IndexDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<IndexDTO>,
        IndexDTO
      >('INDEX', uuid, data);
      if (!response) {
        throw new Error('Failed to update index');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteIndex(uuid: string) {
    try {
      return await this.kafkaClient.delete('INDEX', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Directories
  async getDirectories(): Promise<DirectoryDTO[]> {
    try {
      return await this.kafkaClient.list<DirectoryDTO>('DIRECTORY');
    } catch (error) {
      throw error;
    }
  }

  // Insurance Objects
  async getInsuranceObjects(
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string; // directory UUID
    },
  ): Promise<InsuranceObjectDTO[]> {
    try {
      return await this.kafkaClient.list<InsuranceObjectDTO>(
        'INSURANCE_OBJECT',
        uuids,
        extra,
      );
    } catch (error) {
      throw error;
    }
  }

  async createInsuranceObject(
    insuranceObject: Omit<InsuranceObjectDTO, 'uuid'>,
  ): Promise<InsuranceObjectDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<InsuranceObjectDTO, 'uuid'>,
        InsuranceObjectDTO
      >('INSURANCE_OBJECT', insuranceObject);
      if (!response) {
        throw new Error('Failed to create insurance object');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Files
  async getFiles(
    uuids?: string[],
    extra?: {
      take?: number;
      skip?: number;
      parent_uuid?: string; // agreement UUID
    },
  ): Promise<FileDTO[]> {
    try {
      if (uuids && uuids.length > 200) {
        return await this.fetchInChunks<FileDTO>('FILE', uuids, 200, extra);
      }
      return await this.kafkaClient.list<FileDTO>('FILE', uuids, extra);
    } catch (error) {
      throw error;
    }
  }

  async getFile(uuid: string): Promise<FileDTO> {
    try {
      const response = await this.kafkaClient.read<FileDTO>('FILE', uuid);
      if (!response) {
        throw new Error('File not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createFile(data: Omit<FileDTO, 'uuid'>): Promise<FileDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<FileDTO, 'uuid'>,
        FileDTO
      >('FILE', data);
      if (!response) {
        throw new Error('Failed to create file');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateFile(uuid: string, data: Partial<FileDTO>): Promise<FileDTO> {
    try {
      const response = await this.kafkaClient.update<Partial<FileDTO>, FileDTO>(
        'FILE',
        uuid,
        data,
      );
      if (!response) {
        throw new Error('Failed to update file');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteFile(uuid: string) {
    try {
      await this.kafkaClient.delete('FILE', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Tags

  async bulkUpdateAgreements(
    agreements: AgreementDTO[],
  ): Promise<AgreementDTO[]> {
    try {
      return await this.kafkaClient.bulkUpdate<AgreementDTO, AgreementDTO>(
        'AGREEMENT',
        agreements,
      );
    } catch (error) {
      throw error;
    }
  }

  async getTags(): Promise<TagDTO[]> {
    try {
      return await this.kafkaClient.list<TagDTO>('TAG');
    } catch (error) {
      throw error;
    }
  }

  async getTag(uuid: string): Promise<TagDTO> {
    try {
      const response = await this.kafkaClient.read<TagDTO>('TAG', uuid);
      if (!response) {
        throw new Error('Tag not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createTag(data: Omit<TagDTO, 'uuid'>): Promise<TagDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<TagDTO, 'uuid'>,
        TagDTO
      >('TAG', data);
      if (!response) {
        throw new Error('Failed to create tag');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateTag(uuid: string, data: Partial<TagDTO>): Promise<TagDTO> {
    try {
      return await this.kafkaClient.update<Partial<TagDTO>, TagDTO>(
        'TAG',
        uuid,
        data,
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteTag(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('TAG', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Comments
  async getComments(uuids?: string[]): Promise<CommentDTO[]> {
    try {
      if (uuids && uuids.length > 200) {
        return await this.fetchInChunks<CommentDTO>('COMMENT', uuids, 200);
      }
      const response = await this.kafkaClient.list<CommentDTO>(
        'COMMENT',
        uuids,
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Utility method to fetch entities in chunks for large UUID arrays
  private async fetchInChunks<T>(
    entityType: string,
    uuids: string[],
    chunkSize = 200,
    extra?: Record<string, unknown>,
  ): Promise<T[]> {
    // Split UUIDs into chunks of the specified size
    const chunks: string[][] = [];
    for (let i = 0; i < uuids.length; i += chunkSize) {
      chunks.push(uuids.slice(i, i + chunkSize));
    }

    // Make parallel requests for each chunk
    const results = await Promise.all(
      chunks.map((chunk) => this.kafkaClient.list<T>(entityType, chunk, extra)),
    );

    // Combine all results into a single array
    return results.flat();
  }

  async getComment(uuid: string): Promise<CommentDTO> {
    try {
      const response = await this.kafkaClient.read<CommentDTO>('COMMENT', uuid);
      if (!response) {
        throw new Error('Comment not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createComment(data: Omit<CommentDTO, 'uuid'>): Promise<CommentDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<CommentDTO, 'uuid'>,
        CommentDTO
      >('COMMENT', data);
      if (!response) {
        throw new Error('Failed to create comment');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async bulkCreateComments(
    comments: Omit<CommentDTO, 'uuid'>[],
  ): Promise<CommentDTO[]> {
    try {
      if (comments.length > 200) {
        // Split comments into chunks of 200
        const chunks: Omit<CommentDTO, 'uuid'>[][] = [];
        for (let i = 0; i < comments.length; i += 200) {
          chunks.push(comments.slice(i, i + 200));
        }

        // Process each chunk and combine results
        const results = await Promise.all(
          chunks.map((chunk) =>
            this.kafkaClient.bulkCreate<Omit<CommentDTO, 'uuid'>, CommentDTO>(
              'COMMENT',
              chunk,
            ),
          ),
        );

        // Flatten the results array
        return results.flat();
      }

      return await this.kafkaClient.bulkCreate<
        Omit<CommentDTO, 'uuid'>,
        CommentDTO
      >('COMMENT', comments);
    } catch (error) {
      throw error;
    }
  }

  async updateComment(
    uuid: string,
    data: Partial<CommentDTO>,
  ): Promise<CommentDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<CommentDTO>,
        CommentDTO
      >('COMMENT', uuid, data);
      if (!response) {
        throw new Error('Failed to update comment');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteComment(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('COMMENT', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Processes
  async getProcesses(): Promise<ProcessDTO[]> {
    try {
      return await this.kafkaClient.list<ProcessDTO>('PROCESS');
    } catch (error) {
      throw error;
    }
  }

  async getProcess(uuid: string): Promise<ProcessDTO> {
    try {
      const response = await this.kafkaClient.read<ProcessDTO>('PROCESS', uuid);
      if (!response) {
        throw new Error('Process not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createProcess(data: Omit<ProcessDTO, 'uuid'>): Promise<ProcessDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<ProcessDTO, 'uuid'>,
        ProcessDTO
      >('PROCESS', data);
      if (!response) {
        throw new Error('Failed to create process');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateProcess(
    uuid: string,
    data: Partial<ProcessDTO>,
  ): Promise<ProcessDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<ProcessDTO>,
        ProcessDTO
      >('PROCESS', uuid, data);
      if (!response) {
        throw new Error('Failed to update process');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteProcess(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('PROCESS', uuid);
    } catch (error) {
      throw error;
    }
  }

  // Process Steps
  async getProcessSteps(uuids?: string[]): Promise<ProcessStepDTO[]> {
    try {
      return await this.kafkaClient.list<ProcessStepDTO>('PROCESS_STEP', uuids);
    } catch (error) {
      throw error;
    }
  }

  async getProcessStep(uuid: string): Promise<ProcessStepDTO> {
    try {
      const response = await this.kafkaClient.read<ProcessStepDTO>(
        'PROCESS_STEP',
        uuid,
      );
      if (!response) {
        throw new Error('Process step not found');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createProcessStep(
    data: Omit<ProcessStepDTO, 'uuid'>,
  ): Promise<ProcessStepDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<ProcessStepDTO, 'uuid'>,
        ProcessStepDTO
      >('PROCESS_STEP', data);
      if (!response) {
        throw new Error('Failed to create process step');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateProcessStep(
    uuid: string,
    data: Partial<ProcessStepDTO>,
  ): Promise<ProcessStepDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<ProcessStepDTO>,
        ProcessStepDTO
      >('PROCESS_STEP', uuid, data);
      if (!response) {
        throw new Error('Failed to update process step');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteProcessStep(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('PROCESS_STEP', uuid);
    } catch (error) {
      throw error;
    }
  }

  async bulkCreateProcessSteps(
    steps: Omit<ProcessStepDTO, 'uuid'>[],
  ): Promise<ProcessStepDTO[]> {
    try {
      return await this.kafkaClient.bulkCreate<
        Omit<ProcessStepDTO, 'uuid'>,
        ProcessStepDTO
      >('PROCESS_STEP', steps);
    } catch (error) {
      throw error;
    }
  }

  async bulkUpdateProcessSteps(
    steps: ProcessStepDTO[],
  ): Promise<ProcessStepDTO[]> {
    try {
      return await this.kafkaClient.bulkUpdate<ProcessStepDTO, ProcessStepDTO>(
        'PROCESS_STEP',
        steps,
      );
    } catch (error) {
      throw error;
    }
  }

  async bulkDeleteProcessSteps(uuids: string[]): Promise<ProcessStepDTO[]> {
    try {
      return await this.kafkaClient.bulkDelete<ProcessStepDTO>(
        'PROCESS_STEP',
        uuids,
      );
    } catch (error) {
      throw error;
    }
  }

  // File Validation Settings
  async getFileValidationSettings(): Promise<FileValidationSettingsDTO[]> {
    try {
      return await this.kafkaClient.list<FileValidationSettingsDTO>(
        'FILE_VALIDATION_SETTINGS',
      );
    } catch (error) {
      throw error;
    }
  }

  async updateFileValidationSettings(
    uuid: string,
    settings: Partial<FileValidationSettingsDTO>,
  ): Promise<FileValidationSettingsDTO> {
    try {
      return await this.kafkaClient.update<
        Partial<FileValidationSettingsDTO>,
        FileValidationSettingsDTO
      >('FILE_VALIDATION_SETTINGS', uuid, settings);
    } catch (error) {
      throw error;
    }
  }

  // Directory operations
  async getDirectory(uuid: string): Promise<DirectoryDTO> {
    try {
      return await this.kafkaClient.read<DirectoryDTO>('DIRECTORY', uuid);
    } catch (error) {
      throw error;
    }
  }

  async createDirectory(
    data: Omit<DirectoryDTO, 'uuid'>,
  ): Promise<DirectoryDTO> {
    try {
      const response = await this.kafkaClient.create<
        Omit<DirectoryDTO, 'uuid'>,
        DirectoryDTO
      >('DIRECTORY', data);
      if (!response) {
        throw new Error('Failed to create directory');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateDirectory(
    uuid: string,
    data: Partial<DirectoryDTO>,
  ): Promise<DirectoryDTO> {
    try {
      const response = await this.kafkaClient.update<
        Partial<DirectoryDTO>,
        DirectoryDTO
      >('DIRECTORY', uuid, data);
      if (!response) {
        throw new Error('Failed to update directory');
      }
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteDirectory(uuid: string): Promise<void> {
    try {
      await this.kafkaClient.delete('DIRECTORY', uuid);
    } catch (error) {
      throw error;
    }
  }

  async getFileUrl(fileUrlDTO: FileUrlDTO): Promise<FileUrlResponse> {
    try {
      // Check if there are too many file paths to send in a single request
      if (
        fileUrlDTO.file_name_paths &&
        fileUrlDTO.file_name_paths.length > 200
      ) {
        return await this.fetchFileUrlsInChunks(fileUrlDTO);
      }
      return await this.kafkaClient.create<FileUrlDTO, FileUrlResponse>(
        'blob_storage',
        fileUrlDTO,
      );
    } catch (error) {
      throw error;
    }
  }

  // Utility method to fetch file URLs in chunks for large file_name_paths arrays
  private async fetchFileUrlsInChunks(
    fileUrlDTO: FileUrlDTO,
    chunkSize = 200,
  ): Promise<FileUrlResponse> {
    // Split file paths into chunks
    const allFilePaths = [...fileUrlDTO.file_name_paths];
    const chunks: string[][] = [];

    for (let i = 0; i < allFilePaths.length; i += chunkSize) {
      chunks.push(allFilePaths.slice(i, i + chunkSize));
    }

    // Create a new request for each chunk with the same agreement_uuid
    const chunkRequests = chunks.map((pathsChunk) => ({
      agreement_uuid: fileUrlDTO.agreement_uuid,
      file_name_paths: pathsChunk,
    }));

    // Make parallel requests for each chunk
    const results = await Promise.all(
      chunkRequests.map((chunkRequest) =>
        this.kafkaClient.create<FileUrlDTO, FileUrlResponse>(
          'blob_storage',
          chunkRequest,
        ),
      ),
    );

    // Combine all results by merging the response objects
    // FileUrlResponse is a map where keys are file paths and values are FileUrlItemResponse objects
    const combinedResponse: FileUrlResponse = {};

    // Merge all responses into a single object
    results.forEach((result) => {
      Object.assign(combinedResponse, result);
    });

    return combinedResponse;
  }

  async getGlobalSettings() {
    try {
      const response = await this.kafkaClient.read<GlobalSettingsDTO>(
        'GLOBAL_SETTINGS',
        uuidv4(),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getReportAnalytics(): Promise<ReportAnalytics> {
    try {
      // const response = await api.get(API_ROUTES.REPORT_ANALYTICS);
      // return response.data;
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(mockReportAnalytics);
        }, 4);
      });
    } catch (error) {
      throw error;
    }
  }

  async elasticSearch(params: any): Promise<ElasticSearchResponse> {
    try {
      const response = await api.post<ElasticSearchResponse>(
        `${BASE_URL_API}${API_ROUTES.ELASTIC_SEARCH}`,
        params,
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const apiService = new APIService(useWsClientStore.getState());

export const mockReportAnalytics = {
  effectivenessReport: {
    title: 'Звіт по показникам ефективності',
    description:
      'Перелік користувачів та кількісні показники ефективності роботи з Системою',
    metrics: {
      lateUploadedFiles:
        'кількість договорів або справ, де несвоєчасно додані файли (валідація по даті зйомки і даті завантаження).',
      failedValidationFiles:
        'кількість файлів, що не пройшли контроль завантаження (кількість файлів з помилками).',
    },
  },
  vinReport: {
    title: 'Звіт по виявленим збігам VIN та Д.Н.З.',
    description:
      'Перелік номерів та даних користувачів, які використовували завантаження',
    metrics:
      'VIN номер, Д.Н.3., номери справ, дані користувача (ПІБ/електронна пошта/обліковий номер в системі), який виконував завантаження файлів, дати завантаження для кожної справи.',
  },
};

export type ReportAnalytics = typeof mockReportAnalytics;
